<template lang="pug">
    section
        .overview-grid
            .overview-section
                h3.section-title {{ $t('Summary') }}
                table.bordered-table.panel
                    tr
                        td {{ $t('Products') }}
                        td.min.col-right
                            nobr {{ form.product_count || 0 }}
                    tr
                        td {{ $t('Articles') }}
                        td.min.col-right
                            nobr {{ form.article_count || 0 }}
                    tr
                        td {{ $t('Article quantity') }}
                        td.min.col-right
                            nobr {{ form.article_qty_sum || 0 }}
                    tr
                        td {{ $t('Tax total') }}
                        td.min.col-right
                            nobr {{ form.tax_total | moneyFormat(currency) }}
                    tr
                        td {{ $t('Grand total') }}
                        td.min.col-right
                            nobr {{ form.grand_total | moneyFormat(currency) }}
                    tr
                        td {{ $t('Created at') }}
                        td.min.col-right
                            nobr {{ form.created_at | timestamp2date($t('yyyy-MM-dd') + ', HH:mm') }}
                    tr
                        td {{ $t('Updated at') }}
                        td.min.col-right
                            nobr {{ form.updated_at | timestamp2date($t('yyyy-MM-dd') + ', HH:mm') }}

            .overview-section
                template(v-if="customer && customer.member")
                    h3.section-title
                        router-link.masked-link(v-if="customer.nr" :to="{ name: 'member-form', params: { id: customer.member.id } }")
                            | {{ name }}
                            | &nbsp;
                            small.grey
                                i.el-icon-fa-external-link-alt.fas.gray
                    table.bordered-table.panel
                        tr
                            td {{ $t('Customer number') }}
                            td.min.col-right
                                router-link.masked-link(v-if="customer.nr" :to="{ name: 'member-customer', params: { id: customer.member.id } }")
                                    nobr # {{ customer.nr }}
                        tr
                            td {{ $t('Status') }}
                            td.min.col-right
                                nobr {{ $t(customer.status) }}
                        tr
                            td {{ $t('Email') }}
                            td.min.col-right
                                nobr {{ email }}
                        tr(v-if="customer.member.phone")
                            td {{ $t('Phone') }}
                            td.min.col-right
                                nobr {{ customer.member.phone }}
                        tr(v-if="customerGroupName")
                            td {{ $t('Customer group') }}
                            td.min.col-right
                                nobr {{ customerGroupName }}
                        tr(v-if="customer.tax_free")
                            td {{ $t('Tax free') }}
                            td.min.col-right
                                i.el-icon-fa-check.fas

                template(v-else)
                    h3.section-title {{ $t('Guest') }}
                    table.bordered-table.panel
                        tr
                            td {{ $t('Session') }}
                            td.min.col-right
                                code {{ form.session_id }}

            .overview-section(v-if="customer && customer.member && addresses.length")
                .address-cards
                    address-card(
                        v-for="address in addresses"
                        :key="address.id"
                        :address="address"
                        @set-marker="({ latitude, longitude }) => setMarker(address, latitude, longitude)"
                    )
                        template(slot="title")
                            h3(v-if="isBillingAndShipping(address)")
                                | {{ $t('Billing address & shipping address') }}
                            h3(v-else-if="address.default_billing")
                                | {{ $t('Billing address') }}
                            h3(v-else-if="address.default_shipping")
                                | {{ $t('Shipping address') }}
</template>

<i18n>
{
    "en": {
        "enabled": "Enabled",
        "disabled": "Disabled",
        "blocked": "Blocked"
    },
    "de": {
        "Summary": "Zusammenfassung",
        "Guest": "Gast",
        "Session": "Sitzung",
        "Products": "Produkte",
        "Articles": "Artikel",
        "Article quantity": "Artikelmenge",
        "Tax total": "MwSt. gesamt",
        "Grand total": "Gesamtbetrag brutto",
        "Created at": "Erstellt am",
        "Updated at": "Zuletzt bearbeitet",
        "yyyy-MM-dd": "dd.MM.yyyy",
        "Customer number": "Kundennr.",
        "Customer group": "Kundengruppe",
        "Tax free": "Steuerfrei",
        "Status": "Status",
        "Email": "E-Mail",
        "Phone": "Telefon",
        "enabled": "Aktiv",
        "disabled": "Inaktiv",
        "blocked": "Gesperrt",
        "Billing address & shipping address": "Rechnungs- & Versandadresse",
        "Billing address": "Rechnungsadresse",
        "Shipping address": "Versandadresse"
    }
}
</i18n>

<script>

import _ from 'lodash'
import { moneyFormat, timestamp2date } from '@/filters'

import pinia from '@/pinia';
import { useSetupStore, useShopCartStore } from '@/stores';

import AddressCard from '@/components/AddressCard'

export default {
    name: 'CartMain',
    filters: {
        moneyFormat,
        timestamp2date,
    },
    components: {
        AddressCard,
    },
    computed: {
        customer() {
            return _.get(this.form, 'customer')
        },
        name() {
            var firstName = _.get(this.form, 'customer.member.first_name')
            var lastName = _.get(this.form, 'customer.member.last_name')
            return _.trim((firstName || '') + ' ' + (lastName || ''))
        },
        email() {
            return _.get(this.form, 'customer.member.email')
        },
        addresses() {
            return _.get(this.form, 'customer.member.addresses') || []
        },
        customerGroupName() {
            return _.get(this.form, 'customer.customerGroup.name')
        },
        currency() {
            return _.get(this.form, 'customer.customerGroup.currency.iso4217')
                || _.get(this.activeCms, 'shop.defaultCustomerGroup.currency.iso4217')
                || 'EUR'
        },
        setupStore() {
            return useSetupStore(pinia);
        },
        cartStore() {
            return useShopCartStore(pinia);
        },
        form() {
            return this.cartStore.form;
        },
        activeCms() {
            return this.setupStore.activeCms;
        },
    },
    methods: {
        isBillingAndShipping(address) {
            if (this.addresses.length === 1 && address.type === 'main') return true
            return address.default_billing && address.default_shipping
        },
        setMarker(address, latitude, longitude) {
            var index = _.findIndex(this.addresses, { id: address.id })
            this.cartStore.setFormLatitude({ i: index, value: latitude })
            this.cartStore.setFormLongitude({ i: index, value: longitude })
        },
    },
};
</script>

<style lang="sass" scoped>
.overview-grid
    +res(lg)
        display: flex
        gap: 20px

.overview-section
    flex: 1
    min-width: 0
    & + .overview-section
        margin-top: 20px
        +res(lg)
            margin-top: 0

.section-title
    font-weight: 400
    margin: 0
    margin-bottom: 10px
    small
        font-size: 0.85rem
        position: relative
        top: -1px

.address-cards
    display: flex
    flex-direction: column
    gap: 16px
    :deep(.card)
        max-width: 100%

table.bordered-table.panel
    font-size: 14px
    width: 100%
    place-self: flex-start
    background: $color-body
    border-color: transparent
    border-radius: 4px
    td
        border-color: transparent
        padding: 6px 15px
    tr:first-of-type
        td
            padding-top: 10px
    tr:last-of-type
        td
            padding-bottom: 10px
    .min
        width: 1px
    code
        font-size: 12px
        background: darken($color-body, 5%)
        padding: 2px 6px
        border-radius: 3px
</style>
